import React, { useState } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import SelectTrainingSetWithSearch from '../create/SelectTrainingSetWithSearch';
import ValidTextFieldEB from '../create/ValidTextFieldEB';
import removeTrailingSpaces from '../../upload/util/removeTrailingSpaces';
import useGetDocIdsByEB from '../../hooks/useGetDocIdsByEB';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { IExtractionBatch, ITrainingSet } from '../../../types/my-types';
import { LABEL_LIMIT } from '../../../constants';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';
import { randomStr } from '../../../utils/randomStr';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import RedoIcon from '@mui/icons-material/Redo';
import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

interface IInput {
  documents: string[];
  label: string;
  training_set?: string;
  glynt_tags?: string[];
  is_experimental?: boolean;
}
interface IProps {
  extractionBatches?: IExtractionBatch[];
  extractionBatch: IExtractionBatch;
  groupKey?: string;
  coreVersion: string;
  type: 'menu' | 'icon';
  handleClose?: () => void;
}

function ExtractionBatchReExtract({
  extractionBatches,
  extractionBatch,
  groupKey,
  coreVersion,
  type,
  handleClose,
}: IProps) {
  const { isStaff } = React.useContext(AppContext);
  const [extractionBatchLabel, setExtractionBatchLabel] = useState('');
  const [open, setOpen] = React.useState<boolean>(false);
  const [trainingSet, setTrainingSet] = useState<ITrainingSet>();
  const [networkError, setNetworkError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [isExperimental, setIsExperimental] = useState(false);
  // Track whether we've initialized the label for this modal opening
  const labelInitializedRef = React.useRef(false);
  const glynt = useGlynt();
  const client = useQueryClient();
  const createExtractionBatch = useMutation({
    mutationFn: async (data: any) => await (await glynt).extractionBatch.create(data),

    onSuccess: () => {
      resetStatus();
      client.invalidateQueries({
        queryKey: ['extractionBatches']
      });
    },

    onError: () => {
      setNetworkError('Sorry, something went wrong');
      setExtracting(false);
    }
  });
  const currentTrainingSetId = getIdFromUrl(groupKey ? groupKey : extractionBatch?.training_set);
  let docIds = useGetDocIdsByEB(extractionBatch.id, open, groupKey);

  // Generate label only once when modal opens, not on every extractionBatch change
  React.useEffect(() => {
    if (open && !labelInitializedRef.current && extractionBatch) {
      let label = '';
      if (extractionBatch.label.length >= LABEL_LIMIT - 6) {
        label = `${extractionBatch.label.substr(0, 10)}-${randomStr()}`;
      } else {
        label = `${extractionBatch.label}-${randomStr()}`;
      }
      setExtractionBatchLabel(label);
      labelInitializedRef.current = true;
    }
  }, [open, extractionBatch]);

  // Reset the initialization flag when modal closes
  React.useEffect(() => {
    if (!open) {
      labelInitializedRef.current = false;
    }
  }, [open]);

  const resetStatus = () => {
    setExtracting(false);
    setTrainingSet(undefined);
    setOpen(false);
    setIsExperimental(false);
  };

  const closeModal = () => {
    resetStatus();
    if (handleClose) {
      handleClose();
    }
  };

  const handleSubmit = () => {
    let glynt_tags: string[] = [];
    if (extractionBatch) {
      glynt_tags = extractionBatch.glynt_tags;
    }
    if (!extracting && docIds.length > 0 && extractionBatchLabel.length > 0) {
      setExtracting(true);
      let input: IInput = {
        documents: docIds,
        label: removeTrailingSpaces(extractionBatchLabel),
        is_experimental: isExperimental,
      };
      if (isStaff) {
        input.glynt_tags = glynt_tags;
      }

      input.training_set = trainingSet!.id;

      createExtractionBatch.mutate({ ...input });
    } else {
      alert(
        'Sorry, failed to create extraction batch. Please make sure the extraction batch has documents and the name is unique.'
      );
    }

    if (handleClose) {
      handleClose();
    }
  };

  const onButtonClick = () => {
    if (!extractionBatch.finished) return;
    setOpen(true);
  };

  const isSubmitDisabled =
    extracting ||
    extractionBatchLabel.length === 0 ||
    extractionBatchLabel.length > LABEL_LIMIT ||
    docIds.length === 0 ||
    !trainingSet;

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          id="create-re-extraction-batch"
          data-cy="btn-re-extract-batch"
          key={'Re-Extract'}
          disabled={!extractionBatch.finished}
          onClick={onButtonClick}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
        >
          <ListItemIcon>
            <RedoIcon fontSize="small" />
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Re-Extract</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <IconButtonWhiteBg
          disabled={!extractionBatch.finished}
          onClick={onButtonClick}
          id="icon-re-extraction-batch"
          cypressId="icon-re-extract-batch"
          tooltip="Re-Extract"
        >
          <RedoIcon style={{ fontSize: '15px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : null}

      <DialogGlynt
        open={open}
        closeModal={closeModal}
        cypressId="modal-re-extract"
        width="600px"
        loading={extracting}
      >
        <DialogTitleGlynt
          title="Re-extract"
          closeModal={closeModal}
          loading={extracting}
        />

        <DialogContentGlynt scrollable>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            A new Extraction Batch will be created with the same documents.
          </Typography>

          <FormControlStyled fullWidth data-cy="modal-re-extract-name">
            <FormLabelStyled>Batch Name</FormLabelStyled>
            <ValidTextFieldEB
              placeholder="Enter extraction batch name"
              defaultValue={extractionBatchLabel}
              setLabel={setExtractionBatchLabel}
              disabled={extracting}
              labels={extractionBatches?.map((eb) => eb.label) || []}
              setExtractionBatchLabel={setExtractionBatchLabel}
              width="100%"
            />
          </FormControlStyled>

          <FormControlStyled fullWidth data-cy="modal-re-extract-model">
            <FormLabelStyled>Model Name</FormLabelStyled>
            <SelectTrainingSetWithSearch
              setTrainingSet={setTrainingSet}
              disabled={extracting}
              currentTrainingSetId={currentTrainingSetId}
              width="100%"
            />
          </FormControlStyled>

          <FormControlStyled fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExperimental}
                  onChange={(e) => setIsExperimental(e.target.checked)}
                  disabled={extracting}
                  data-cy="is-experimental-checkbox"
                />
              }
              label="Experimental"
            />
          </FormControlStyled>

          {networkError.length > 0 && (
            <Typography color="error" sx={{ mt: 2 }}>
              {networkError}
            </Typography>
          )}
        </DialogContentGlynt>

        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          cancelCypressId="modal-re-extract-cancel"
          submitCypressId="btn-create-extraction-batch"
          clearCypressId="modal-re-extract-clear"
          submitButtonText="Create Extraction Batch"
          submitLoading={extracting}
          submitDisabled={isSubmitDisabled}
        />
      </DialogGlynt>
    </>
  );
}

export default ExtractionBatchReExtract;
